import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  resource,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location as BrowserLocation } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { LocationsService } from '../locations.service';
import { GeocodeMatch, GeocodingService } from '../geocoding.service';
import {
  GeoLocation,
  LocationInput,
  Weekday,
  WorkingHours,
  WEEKDAYS,
  formatAddress,
  formatDayHours,
  isActive,
  locationMapQuery,
} from '../location.model';
import { CompanyService } from '../../../shared/services/company.service';
import { IconComponent } from '../../../shared/icon/icon.component';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { AvatarPickerComponent } from '../../../shared/components/avatar-picker/avatar-picker.component';
import { AvatarService } from '../../../shared/services/avatar.service';
import { environment } from '../../../../environments/environment';

type TextField =
  | 'name'
  | 'streetName'
  | 'postalCode'
  | 'city'
  | 'phone'
  | 'email'
  | 'website'
  | 'description';

/** Comparison key for addresses: whitespace and case changes don't need a new lookup. */
const addressKey = (address: string) => address.trim().replace(/\s+/g, ' ').toLowerCase();

/** Views, edits and creates a location (`locations/new` opens an empty form). */
@Component({
  selector: 'app-location-editor',
  imports: [ReactiveFormsModule, IconComponent, ConfirmDialogComponent, AvatarComponent, AvatarPickerComponent],
  templateUrl: './location-editor.component.html',
  styleUrl: './location-editor.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationEditorComponent {
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #locationsService = inject(LocationsService);
  readonly #geocoding = inject(GeocodingService);
  readonly #companyService = inject(CompanyService);
  readonly #avatarService = inject(AvatarService);
  readonly #browserLocation = inject(BrowserLocation);
  readonly #fb = inject(FormBuilder);
  readonly #sanitizer = inject(DomSanitizer);

  readonly #id = this.#route.snapshot.paramMap.get('id');

  protected readonly isNew = !this.#id;
  protected readonly isEditing = signal(this.isNew);
  protected readonly isSaving = signal(false);
  // A new location's page can open with an error passed from create (e.g. avatar upload failed)
  protected readonly errorMessage = signal<string | null>(
    (this.#router.currentNavigation()?.extras.state?.['errorMessage'] as string | undefined) ?? null,
  );

  /** Chosen in the picker, uploaded after the location is saved. */
  protected readonly avatarFile = signal<File | null>(null);
  protected readonly showLightbox = signal(false);

  protected readonly locationResource = resource({
    params: () => this.#id ?? undefined, // undefined params: loader is skipped (new location)
    loader: ({ params: id }) => this.#locationsService.getLocation(id),
  });

  // --- Address: geocoded on Show on map and on Save; only an exact match can be saved ---

  /** Pin of the last lookup while editing (Google's match, exact or not). */
  protected readonly previewGeo = signal<GeoLocation | null>(null);
  /** Why the last lookup can't be saved; null when it can. */
  protected readonly addressWarning = signal<string | null>(null);
  protected readonly isLocating = signal(false);
  /** Last lookup, reused while the address doesn't change (seeded from saved coordinates on edit). */
  #lastLookup: { address: string; match: GeocodeMatch } | null = null;

  /** What the map shows, as a string: an unchanged string stops here, so the iframe doesn't reload. */
  readonly #mapQuery = computed(() => {
    const geo = this.isEditing() ? this.previewGeo() : null;
    const location = this.locationResource.value();
    return geo
      ? `${geo.latitude},${geo.longitude}`
      : location?.streetName && location.city
        ? locationMapQuery(location)
        : null;
  });

  // Sanitizing creates a new object every time, so it only runs when the query string changes
  protected readonly mapUrl = computed(() => {
    const query = this.#mapQuery();
    return query
      ? this.#sanitizer.bypassSecurityTrustResourceUrl(
          `https://www.google.com/maps/embed/v1/place?key=${environment.googleMapsApiKey}&q=${encodeURIComponent(query)}`,
        )
      : null;
  });

  protected readonly weekdays = WEEKDAYS;
  protected readonly formatDayHours = formatDayHours;
  protected readonly isActive = isActive;

  // --- Status: locations are disabled, never deleted (orders reference them) ---

  protected readonly confirmingDisable = signal(false);
  protected readonly isTogglingActive = signal(false);

  protected readonly form = this.#fb.nonNullable.group({
    name: ['', [Validators.required]],
    streetName: ['', [Validators.required]],
    city: ['', [Validators.required]],
    postalCode: [''],
    phone: [''],
    email: ['', [Validators.email]],
    website: [''],
    description: [''],
    workingHours: this.#fb.group({
      monday: this.#fb.array<FormGroup>([]),
      tuesday: this.#fb.array<FormGroup>([]),
      wednesday: this.#fb.array<FormGroup>([]),
      thursday: this.#fb.array<FormGroup>([]),
      friday: this.#fb.array<FormGroup>([]),
      saturday: this.#fb.array<FormGroup>([]),
      sunday: this.#fb.array<FormGroup>([]),
    }),
  });

  protected get f() {
    return this.form.controls;
  }

  constructor() {
    if (this.isNew) {
      void this.#prefillFromCompany();
    }
  }

  /** Most companies have a single location, so start a new one at the company's address. */
  async #prefillFromCompany() {
    try {
      const address = (await this.#companyService.getCurrentCompany())?.address;
      const { streetName, postalCode, city } = this.form.controls;
      // Don't overwrite anything the user already typed while the company was loading
      if (!address || streetName.dirty || postalCode.dirty || city.dirty) {
        return;
      }
      this.form.patchValue({
        streetName: address.streetName,
        postalCode: address.postalCode ?? '',
        city: address.city,
      });
      await this.showOnMap();
    } catch (error) {
      console.warn('Could not load company address for prefill', error);
    }
  }

  #currentAddressKey(): string {
    return addressKey(formatAddress(this.form.getRawValue()));
  }

  /** Geocodes the form's address (or reuses the last lookup) and pins it; returns coordinates only for an exact match. */
  async #locate(): Promise<GeoLocation | null> {
    const { streetName, city } = this.form.getRawValue();
    if (!streetName.trim() || !city.trim()) {
      this.addressWarning.set('Enter street and city first.');
      return null;
    }

    const address = this.#currentAddressKey();
    const match =
      this.#lastLookup?.address === address
        ? this.#lastLookup.match
        : await this.#geocoding.geocode(formatAddress(this.form.getRawValue()));
    // Failed lookups aren't remembered, so the user can retry
    if (match) {
      this.#lastLookup = { address, match };
    }

    this.previewGeo.set(match?.geo ?? null);
    this.addressWarning.set(
      match?.exact
        ? null
        : match
          ? `Exact address not found (closest match: "${match.matchedAddress}"). Check the street and house number.`
          : 'Address not found on the map. Check the street, house number, postal code and city.',
    );
    return match?.exact ? match.geo : null;
  }

  protected async showOnMap() {
    if (this.#lastLookup?.address === this.#currentAddressKey()) {
      return; // unchanged address: the map and warning already show the last result
    }
    this.isLocating.set(true);
    try {
      await this.#locate();
    } finally {
      this.isLocating.set(false);
    }
  }

  #resetAddressCheck() {
    this.previewGeo.set(null);
    this.addressWarning.set(null);
    this.#lastLookup = null;
  }

  protected intervalsFor(day: Weekday): FormArray {
    return this.form.controls.workingHours.controls[day];
  }

  #newIntervalGroup(open = '', close = ''): FormGroup {
    return this.#fb.nonNullable.group({ open: [open], close: [close] });
  }

  protected addInterval(day: Weekday) {
    this.intervalsFor(day).push(this.#newIntervalGroup());
  }

  protected removeInterval(day: Weekday, index: number) {
    this.intervalsFor(day).removeAt(index);
  }

  #patchWorkingHours(workingHours?: WorkingHours) {
    for (const { key } of this.weekdays) {
      const array = this.intervalsFor(key);
      array.clear();
      for (const interval of workingHours?.[key] ?? []) {
        array.push(this.#newIntervalGroup(interval.open, interval.close));
      }
    }
  }

  #toWorkingHours(): WorkingHours {
    const result: WorkingHours = {};
    for (const { key } of this.weekdays) {
      const intervals = this.intervalsFor(key)
        .getRawValue()
        .filter((i) => i.open && i.close);
      if (intervals.length) {
        result[key] = intervals;
      }
    }
    return result;
  }

  protected startEdit() {
    const location = this.locationResource.value();
    if (!location) {
      return;
    }
    this.errorMessage.set(null);
    this.#resetAddressCheck();
    this.form.reset();
    this.form.patchValue(location);
    this.#patchWorkingHours(location.workingHours);
    // Saved coordinates belong to the saved address: no lookup needed until the address changes
    if (location.geoLocation) {
      const saved = formatAddress(location);
      this.#lastLookup = {
        address: addressKey(saved),
        match: { geo: location.geoLocation, exact: true, matchedAddress: saved },
      };
    }
    this.isEditing.set(true);
  }

  protected cancelEdit() {
    this.avatarFile.set(null);
    this.errorMessage.set(null);
    this.#resetAddressCheck();
    if (this.isNew) {
      this.goBack();
      return;
    }
    this.isEditing.set(false);
  }

  protected openLightbox() {
    if (this.locationResource.value()?.avatarUrl) {
      this.showLightbox.set(true);
    }
  }

  protected closeLightbox() {
    this.showLightbox.set(false);
  }

  /** Escape in a field: back to the saved value (empty for a new location). */
  protected revertField(field: TextField, event: Event) {
    const saved = this.locationResource.value()?.[field] ?? '';
    this.form.controls[field].setValue(saved);
    (event.target as HTMLElement).blur();
  }

  protected async save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.isSaving()) {
      return;
    }

    this.isSaving.set(true);
    this.errorMessage.set(null);

    // Only an exact address can be saved, for new and edited locations alike
    const geoLocation = await this.#locate();
    if (!geoLocation) {
      this.isSaving.set(false); // the warning explains why
      return;
    }

    const input = {
      ...this.form.getRawValue(),
      workingHours: this.#toWorkingHours(),
      geoLocation,
    } satisfies LocationInput;

    let id: string;
    try {
      if (this.isNew) {
        id = await this.#locationsService.createLocation(input);
      } else {
        id = this.#id!;
        await this.#locationsService.updateLocation(id, input);
      }
    } catch (error) {
      console.error('Error saving location:', error);
      this.errorMessage.set('Failed to save location. Please try again.');
      this.isSaving.set(false);
      return;
    }

    // The location is saved; now the avatar can go to its id. A failure here keeps the saved location.
    const avatarError = await this.#uploadPendingAvatar(id);

    this.isSaving.set(false);
    this.#resetAddressCheck();
    if (this.isNew) {
      await this.#router.navigate(['/main/locations', id], {
        replaceUrl: true,
        state: avatarError ? { errorMessage: avatarError } : undefined,
      });
      return;
    }
    this.errorMessage.set(avatarError);
    this.locationResource.reload();
    this.isEditing.set(false);
  }

  /** Uploads the locally kept avatar for a saved location. Returns an error message on failure. */
  async #uploadPendingAvatar(id: string): Promise<string | null> {
    const file = this.avatarFile();
    if (!file) {
      return null;
    }
    try {
      const avatar = await this.#avatarService.upload(`locations/${id}`, file);
      await this.#locationsService.updateLocation(id, avatar);
      return null;
    } catch (error) {
      console.error('Error uploading location avatar:', error);
      return 'Location saved, but the avatar upload failed. Edit the location to try again.';
    } finally {
      this.avatarFile.set(null);
    }
  }

  /** Disable (after the confirm dialog) or enable the location. */
  protected async setActive(active: boolean) {
    const location = this.locationResource.value();
    if (!location || this.isTogglingActive()) {
      return;
    }

    this.isTogglingActive.set(true);
    this.errorMessage.set(null);
    try {
      await this.#locationsService.setActive(location.id, active);
      this.locationResource.update((loc) => loc && { ...loc, active });
      this.confirmingDisable.set(false);
    } catch (error) {
      console.error('Error changing location status:', error);
      this.errorMessage.set(`Failed to ${active ? 'enable' : 'disable'} the location. Please try again.`);
    } finally {
      this.isTogglingActive.set(false);
    }
  }

  protected goBack() {
    this.#browserLocation.back();
  }
}
